import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Activity, Loader2, X } from 'lucide-react';
import { Tooltip } from '@/components/ui/Tooltip';
import {
	LATEST_AUDIT_LOGS_POLL_MS,
	MODAL_LATEST_AUDIT_LOGS_COUNT,
	useGetLatestAuditLogs,
} from '@/services';
import { formatProfessionalFullName } from '@/utils';
import { cn } from '@/lib/utils';
import {
	AUDIT_LOG_ACTION_DESCRIPTION,
	AUDIT_LOG_ACTION_LABEL,
	AUDIT_LOG_CATEGORY_DESCRIPTION,
	AUDIT_LOG_CATEGORY_LABEL,
	AUDIT_LOG_CATEGORY_STYLES,
	formatAuditMetadataBody,
	formatAuditMetadataSummary,
	formatAuditOccurredAtDetail,
	formatAuditRelativeTime,
	hasAuditMetadata,
	resolveAuditLogIcon,
} from '@/utils/auditLogDisplay';

const SCROLL_CLASS = cn(
	'overflow-y-auto overscroll-contain scroll-smooth [scrollbar-gutter:stable]',
	'[scrollbar-width:thin] [scrollbar-color:theme(colors.zinc.300)_transparent]',
	'[&::-webkit-scrollbar]:w-1.5',
	'[&::-webkit-scrollbar-track]:bg-transparent',
	'[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-300',
	'hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400',
);

type AdminActivityModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

const AdminAuditTimelineItem = ({
	entry,
	isLast,
}: {
	entry: AuditLogs.AuditLogLatestItem;
	isLast: boolean;
}) => {
	const styles = AUDIT_LOG_CATEGORY_STYLES[entry.category];
	const Icon = resolveAuditLogIcon(entry.category, entry.action);
	const categoryLabel = AUDIT_LOG_CATEGORY_LABEL[entry.category];
	const actionLabel = AUDIT_LOG_ACTION_LABEL[entry.action];
	const metadataSummary = formatAuditMetadataSummary(
		entry.action,
		entry.metadata,
	);
	const showMetadata = hasAuditMetadata(entry.metadata);
	const occurredAtDetail = formatAuditOccurredAtDetail(entry.occurred_at);
	const professionalName = entry.professional
		? formatProfessionalFullName(entry.professional)
		: null;

	const metadataTooltip = (
		<pre className="max-h-48 max-w-xs overflow-auto whitespace-pre-wrap break-all text-left text-[11px] leading-relaxed">
			{formatAuditMetadataBody(entry.metadata, entry.action)}
		</pre>
	);

	const dateTooltip = (
		<div className="space-y-0.5 text-left text-xs leading-relaxed">
			{occurredAtDetail.weekday ? (
				<p className="font-semibold">{occurredAtDetail.weekday}</p>
			) : null}
			<p className="tabular-nums">{occurredAtDetail.time}</p>
			<p>{occurredAtDetail.date}</p>
		</div>
	);

	return (
		<li className="relative flex items-center gap-4 pb-6 last:pb-0">
			{!isLast ? (
				<span
					className={cn(
						'absolute bottom-0 left-[1.125rem] top-9 w-px -translate-x-1/2',
						styles.line,
					)}
					aria-hidden
				/>
			) : null}

			<div
				className={cn(
					'relative z-[1] flex size-9 shrink-0 items-center justify-center rounded-full text-white shadow-sm ring-4',
					styles.dot,
				)}
			>
				<Icon className="size-4" strokeWidth={2.25} aria-hidden />
			</div>

			<div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
				<Tooltip
					content={AUDIT_LOG_CATEGORY_DESCRIPTION[entry.category]}
					className="max-w-xs whitespace-normal text-left"
					interactive
				>
					<span
						className={cn(
							'inline-flex shrink-0 cursor-help items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1',
							styles.badge,
						)}
					>
						{categoryLabel}
					</span>
				</Tooltip>

				<Tooltip
					content={AUDIT_LOG_ACTION_DESCRIPTION[entry.action]}
					className="max-w-xs whitespace-normal text-left"
					interactive
				>
					<span className="shrink-0 cursor-help text-xs font-semibold text-zinc-900 underline decoration-zinc-300 decoration-dotted underline-offset-2">
						{actionLabel}
					</span>
				</Tooltip>

				{professionalName ? (
					<span className="max-w-[10rem] shrink-0 truncate text-xs font-medium text-zinc-500">
						{professionalName}
					</span>
				) : null}

				<div className="min-w-0 flex-1">
					{metadataSummary || showMetadata ? (
						<Tooltip
							content={metadataTooltip}
							className="max-w-sm whitespace-normal"
							position="bottom"
							interactive
							hideDelay={200}
						>
							<div className="inline-block w-fit max-w-full cursor-help truncate rounded-lg border border-zinc-100 bg-zinc-50/60 px-2 py-0.5 text-[11px] text-zinc-400 transition-colors hover:border-zinc-200 hover:bg-zinc-100/70">
								{metadataSummary ?? 'parâmetros'}
							</div>
						</Tooltip>
					) : null}
				</div>

				<Tooltip
					content={dateTooltip}
					className="whitespace-normal text-left"
					position="top"
					interactive
				>
					<time
						dateTime={entry.occurred_at}
						className="shrink-0 cursor-help whitespace-nowrap text-[11px] font-medium text-zinc-500 underline decoration-zinc-300 decoration-dotted underline-offset-2"
					>
						{formatAuditRelativeTime(entry.occurred_at)}
					</time>
				</Tooltip>
			</div>
		</li>
	);
};

export const AdminActivityModal = ({
	isOpen,
	onClose,
}: AdminActivityModalProps) => {
	const { data, isLoading, isError, isFetching } = useGetLatestAuditLogs(
		{ last: MODAL_LATEST_AUDIT_LOGS_COUNT },
		{
			enabled: isOpen,
			refetchInterval: isOpen ? LATEST_AUDIT_LOGS_POLL_MS : false,
		},
	);

	const events = data?.data ?? [];

	useEffect(() => {
		if (!isOpen) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};

		document.body.style.overflow = 'hidden';
		window.addEventListener('keydown', onKeyDown);

		return () => {
			document.body.style.overflow = 'auto';
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
				aria-hidden
			/>
			<div
				className="animate-in zoom-in-95 relative flex h-[75vh] w-[75vw] max-w-[75vw] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200"
				role="dialog"
				aria-modal="true"
				aria-labelledby="admin-activity-modal-title"
			>
				<div className="flex shrink-0 items-start justify-between gap-4 border-b border-stone-100 px-8 py-6">
					<div className="flex min-w-0 items-center gap-4">
						<div className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-stone-50 text-stone-700">
							<Activity
								className="size-5"
								strokeWidth={2.25}
								aria-hidden
							/>
						</div>
						<div className="min-w-0">
							<h2
								id="admin-activity-modal-title"
								className="font-albert truncate text-xl font-bold text-stone-900"
							>
								Atividade recente
							</h2>
							<p className="text-sm text-stone-500">
								Últimos {MODAL_LATEST_AUDIT_LOGS_COUNT} eventos
								· atualiza a cada{' '}
								{LATEST_AUDIT_LOGS_POLL_MS / 1000}s
							</p>
						</div>
					</div>
					<div className="flex shrink-0 items-center gap-3">
						{isLoading || (isFetching && events.length === 0) ? (
							<Loader2
								className="size-4 animate-spin text-stone-400"
								aria-label="Carregando"
							/>
						) : isFetching ? (
							<Loader2
								className="size-4 animate-spin text-stone-400"
								aria-label="Atualizando"
							/>
						) : isError ? null : (
							<span
								className="relative flex size-2.5 shrink-0"
								aria-label="Monitoramento ativo"
							>
								<span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-40" />
								<span className="relative inline-flex size-2.5 rounded-full bg-emerald-500" />
							</span>
						)}
						<button
							type="button"
							onClick={onClose}
							className="shrink-0 text-stone-400 transition-colors hover:text-stone-600"
							aria-label="Fechar"
						>
							<X size={20} />
						</button>
					</div>
				</div>

				<div className={cn('min-h-0 flex-1 px-8 py-6', SCROLL_CLASS)}>
					{isLoading || (isFetching && events.length === 0) ? (
						<div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-16 text-sm text-stone-500">
							<Loader2
								className="size-4 animate-spin"
								aria-hidden
							/>
							Carregando atividade…
						</div>
					) : isError ? (
						<p className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-10 text-center text-sm text-rose-800">
							Não foi possível carregar os eventos de auditoria.
						</p>
					) : events.length === 0 ? (
						<p className="rounded-xl border border-dashed border-stone-200 bg-stone-50 px-4 py-12 text-center text-sm text-stone-500">
							Nenhum evento registrado ainda.
						</p>
					) : (
						<ul className="relative">
							{events.map((entry, index) => (
								<AdminAuditTimelineItem
									key={entry.id}
									entry={entry}
									isLast={index === events.length - 1}
								/>
							))}
						</ul>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};
